import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Descriptions,
  Table,
  Tag,
  Button,
  Spin,
  message,
  Card,
  Row,
  Col,
  Statistic,
  Modal,
  Form,
  Input,
  Select,
  Segmented,
  Space,
  Popconfirm,
  Typography,
  Tooltip as AntTooltip,
  Upload,
  Image,
  Avatar,
} from 'antd';
import type { UploadProps, RcFile } from 'antd/es/upload';
import {
  ArrowLeftOutlined,
  CloudDownloadOutlined,
  WifiOutlined,
  PlusOutlined,
  UserOutlined,
  InfoCircleOutlined,
  UploadOutlined,
  PictureOutlined,
} from '@ant-design/icons';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import {
  getAsset,
  getUsers,
  getOrganizations,
  moveAssetToOrganization,
  createUser,
  updateSimFriendlyName,
  getLogoUploadUrl,
  setAssetLogo,
  removeAssetLogo,
  Asset,
  Organization,
  User,
} from '../../services/adminService';
import { ApiClient } from '../../services/apiClient';
import { useAuth } from '../../auth/AuthContext';
import LookupSelect from '../../components/LookupSelect';
import PageHelp from '../../components/PageHelp';
import YachtUserTree from '../../components/YachtUserTree';
import { providerLabel } from '../../utils/providerLabel';

const FRIENDLY_NAME_EDIT_ROLES = [
  'super_admin',
  'reseller_admin',
  'reseller_support',
  'customer_admin',
  'asset_manager',
];

interface SimAssignment {
  id: number;
  identifier: string;
  provider: string;
  msisdn: string | null;
  alias: string | null;
  status: string | null;
  sim_type: string | null;
  friendly_name: string | null;
  assigned_at: string;
}

interface UsageSummary {
  iccid: string;
  msisdn: string;
  alias1: string | null;
  status: string;
  days_with_usage: number;
  total_data_bytes: string;
  total_data_gb: string;
  first_usage: string;
  last_usage: string;
}

interface UsageRecord {
  iccid: string;
  usage_date: string;
  data_volume_bytes: number;
  data_volume_gb: string;
  alias1: string | null;
}

const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${units[i]}`;
};

const SIM_COLORS = [
  '#1890ff',
  '#52c41a',
  '#faad14',
  '#f5222d',
  '#722ed1',
  '#13c2c2',
  '#eb2f96',
  '#fa8c16',
  '#2f54eb',
  '#a0d911',
  '#1677ff',
  '#ff7a45',
  '#597ef7',
  '#36cfc9',
];

interface CrewMember {
  id: number;
  user_id: number;
  department: string | null;
  role_on_asset: string;
  email: string;
  first_name: string;
  last_name: string;
  user_role: string;
  created_at: string;
}

const AssetDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { role: myRole } = useAuth();
  const canManageCrew =
    myRole === 'super_admin' ||
    myRole === 'reseller_admin' ||
    myRole === 'customer_admin' ||
    myRole === 'asset_manager';
  const canEditFriendlyName = !!myRole && FRIENDLY_NAME_EDIT_ROLES.includes(myRole);
  const canMoveAssetOrg =
    myRole === 'super_admin' || myRole === 'reseller_admin' || myRole === 'customer_admin';
  const [asset, setAsset] = useState<Asset | null>(null);
  const [simAssignments, setSimAssignments] = useState<SimAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [usageSummaries, setUsageSummaries] = useState<UsageSummary[]>([]);
  const [usageRecords, setUsageRecords] = useState<UsageRecord[]>([]);
  const [usageLoading, setUsageLoading] = useState(false);
  const [crew, setCrew] = useState<CrewMember[]>([]);
  const [crewLoading, setCrewLoading] = useState(false);
  const [addCrewVisible, setAddCrewVisible] = useState(false);
  const [addCrewMode, setAddCrewMode] = useState<'existing' | 'create'>('existing');
  const [addCrewSubmitting, setAddCrewSubmitting] = useState(false);
  const [allUsers, setAllUsers] = useState<User[]>([]);

  // Move-to-org modal
  const [moveOrgVisible, setMoveOrgVisible] = useState(false);
  const [moveOrgTarget, setMoveOrgTarget] = useState<number | null>(null);
  const [moveOrgSubmitting, setMoveOrgSubmitting] = useState(false);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [addCrewForm] = Form.useForm();

  // KAN-19: vessel logo upload state
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoRemoving, setLogoRemoving] = useState(false);

  const canManageLogo =
    myRole === 'super_admin' ||
    myRole === 'reseller_admin' ||
    myRole === 'customer_admin' ||
    myRole === 'asset_manager';

  const assetId = Number(id);

  const refreshAsset = useCallback(async () => {
    const data = await getAsset(assetId);
    setAsset(data.asset);
    setSimAssignments(data.simAssignments || []);
  }, [assetId]);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      try {
        const data = await getAsset(assetId);
        if (cancelled) return;
        setAsset(data.asset);
        setSimAssignments(data.simAssignments || []);
      } catch (err: any) {
        if (!cancelled) message.error(err.message || 'Failed to load asset');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [assetId]);

  // Fetch crew
  const fetchCrew = async () => {
    setCrewLoading(true);
    try {
      const data = await ApiClient.get<{ crew: CrewMember[] }>(`/admin/assets/${assetId}/crew`);
      setCrew(data.crew || []);
    } catch {
      setCrew([]);
    } finally {
      setCrewLoading(false);
    }
  };

  useEffect(() => {
    fetchCrew();
    // eslint-disable-next-line
  }, [assetId]);

  const handleAddCrew = async (values: any) => {
    setAddCrewSubmitting(true);
    try {
      let userId: number;
      if (addCrewMode === 'create') {
        // Create a new user in this asset's organization, then immediately
        // assign them to the asset.
        const created = await createUser({
          email: values.email,
          firstName: values.firstName,
          lastName: values.lastName,
          role: values.userRole || 'crew',
          organizationId: asset!.organization_id,
          department: values.department || undefined,
        });
        userId = created.id;
      } else {
        userId = values.userId;
      }
      await ApiClient.post(`/admin/assets/${assetId}/crew`, {
        userId,
        roleOnAsset: values.roleOnAsset || 'crew',
        department: values.department,
      });
      message.success('Crew member added');
      setAddCrewVisible(false);
      addCrewForm.resetFields();
      fetchCrew();
    } catch (err: any) {
      message.error(err.message || 'Failed to add crew');
    } finally {
      setAddCrewSubmitting(false);
    }
  };

  const handleRemoveCrew = async (crewUserId: number) => {
    try {
      await ApiClient.delete(`/admin/assets/${assetId}/crew/${crewUserId}`);
      message.success('Crew member removed');
      fetchCrew();
    } catch (err: any) {
      message.error(err.message || 'Failed to remove crew');
    }
  };

  const openAddCrew = async () => {
    setAddCrewMode('existing');
    addCrewForm.resetFields();
    try {
      const users = await getUsers();
      setAllUsers(users.filter(u => u.is_active && u.organization_id === asset?.organization_id));
    } catch {
      /* ignore */
    }
    setAddCrewVisible(true);
  };

  const openMoveOrg = async () => {
    try {
      const orgs = await getOrganizations();
      setOrganizations(orgs.filter(o => o.is_active && o.id !== asset?.organization_id));
    } catch (err: any) {
      message.error(err.message || 'Failed to load organizations');
    }
    setMoveOrgTarget(null);
    setMoveOrgVisible(true);
  };

  const handleMoveOrg = async () => {
    if (!asset || !moveOrgTarget) return;
    setMoveOrgSubmitting(true);
    try {
      const result = await moveAssetToOrganization(asset.id, moveOrgTarget);
      message.success(
        `Moved asset to new organization (${result.moved_sims} SIM(s), ${result.moved_users} user(s))`
      );
      setMoveOrgVisible(false);
      // Refetch the asset to pick up the new organization_id + org_name
      const data = await getAsset(asset.id);
      setAsset(data.asset);
      setSimAssignments(data.simAssignments || []);
    } catch (err: any) {
      message.error(err.message || 'Failed to move asset');
    } finally {
      setMoveOrgSubmitting(false);
    }
  };

  // KAN-19: vessel logo handlers
  const ALLOWED_LOGO_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
  const MAX_LOGO_BYTES = 2 * 1024 * 1024; // 2MB

  const validateLogoFile = (file: RcFile): boolean => {
    if (!ALLOWED_LOGO_TYPES.includes(file.type)) {
      message.error('Logo must be a PNG, JPEG, or WebP image.');
      return false;
    }
    if (file.size > MAX_LOGO_BYTES) {
      message.error('Logo must be 2MB or smaller.');
      return false;
    }
    return true;
  };

  const handleLogoUpload: UploadProps['customRequest'] = async options => {
    const { file, onSuccess, onError } = options;
    const rcFile = file as RcFile;
    if (!validateLogoFile(rcFile)) {
      const err = new Error('Invalid logo file');
      onError?.(err);
      return;
    }
    setLogoUploading(true);
    try {
      // 1. Ask our API for a presigned S3 PUT URL.
      const { uploadUrl, s3Key } = await getLogoUploadUrl(assetId, rcFile.type);
      // 2. Upload the raw bytes directly to S3 (NOT our API — raw fetch is correct).
      const putRes = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': rcFile.type },
        body: rcFile,
      });
      if (!putRes.ok) {
        throw new Error(`Upload failed (${putRes.status})`);
      }
      // 3. Tell our API the upload is committed.
      await setAssetLogo(assetId, s3Key);
      message.success('Vessel logo updated.');
      onSuccess?.({}, new XMLHttpRequest());
      await refreshAsset();
    } catch (err: any) {
      console.warn('Logo upload failed', err);
      message.error('Failed to upload vessel logo. Please try again.');
      onError?.(err instanceof Error ? err : new Error('Logo upload failed'));
    } finally {
      setLogoUploading(false);
    }
  };

  const handleRemoveLogo = async () => {
    setLogoRemoving(true);
    try {
      await removeAssetLogo(assetId);
      message.success('Vessel logo removed.');
      await refreshAsset();
    } catch (err: any) {
      console.warn('Logo removal failed', err);
      message.error('Failed to remove vessel logo. Please try again.');
    } finally {
      setLogoRemoving(false);
    }
  };

  // Fetch usage data for VRS SIMs
  useEffect(() => {
    const fetchUsage = async () => {
      const vrsSims = simAssignments.filter(s => s.provider === 'vrs');
      if (vrsSims.length === 0) return;

      setUsageLoading(true);
      try {
        const iccids = vrsSims.map(s => s.identifier);
        // Fetch summary for all SIMs
        const summaryPromises = iccids.map(iccid =>
          ApiClient.get<{ data: UsageSummary[] }>(`/vrs/usage/summary?iccid=${iccid}`).catch(
            () => ({ data: [] })
          )
        );
        const summaryResults = await Promise.all(summaryPromises);
        const summaries = summaryResults.flatMap(r => r.data || []);
        setUsageSummaries(summaries);

        // Fetch last 30 days usage for chart
        const now = new Date();
        const start = new Date(now.getTime() - 30 * 86400000);
        const startDate = start.toISOString().split('T')[0];
        const endDate = now.toISOString().split('T')[0];

        const usagePromises = iccids.map(iccid =>
          ApiClient.get<{ data: UsageRecord[] }>(
            `/vrs/usage?iccid=${iccid}&startDate=${startDate}&endDate=${endDate}&limit=500`
          ).catch(() => ({ data: [] }))
        );
        const usageResults = await Promise.all(usagePromises);
        const allRecords = usageResults.flatMap(r => r.data || []);
        setUsageRecords(allRecords);
      } catch {
        // Silently fail
      } finally {
        setUsageLoading(false);
      }
    };
    if (simAssignments.length > 0) fetchUsage();
  }, [simAssignments]);

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!asset) {
    return (
      <div style={{ padding: 24 }}>
        <p>Asset not found.</p>
        <Button onClick={() => navigate('/admin/orgs-assets')}>Back to Org &amp; Assets</Button>
      </div>
    );
  }

  // Build chart data: daily total across all SIMs
  const chartDataMap = new Map<string, Record<string, number>>();
  const simLabels = new Set<string>();
  for (const r of usageRecords) {
    const date = new Date(r.usage_date).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
    });
    const label = r.alias1 || r.iccid.slice(-6);
    simLabels.add(label);
    if (!chartDataMap.has(date)) chartDataMap.set(date, {});
    const entry = chartDataMap.get(date)!;
    entry[label] = (entry[label] || 0) + parseFloat(r.data_volume_gb || '0');
  }
  const chartData = [...chartDataMap.entries()]
    .sort((a, b) => {
      // Sort by original date
      const parseDate = (d: string) => {
        const parts = d.split(' ');
        return new Date(`${parts[1]} ${parts[0]}, 2026`);
      };
      return parseDate(a[0]).getTime() - parseDate(b[0]).getTime();
    })
    .map(([date, values]) => ({ date, ...values }));
  const simLabelArray = [...simLabels];

  const totalDataBytes = usageSummaries.reduce(
    (sum, s) => sum + parseFloat(s.total_data_bytes || '0'),
    0
  );
  const activeSims = usageSummaries.filter(s => s.days_with_usage > 0).length;

  const handleFriendlyNameChange = async (assignmentId: number, value: string) => {
    const trimmed = (value ?? '').trim();
    const next = trimmed === '' ? null : trimmed;
    try {
      await updateSimFriendlyName(assignmentId, next);
      setSimAssignments(prev =>
        prev.map(s => (s.id === assignmentId ? { ...s, friendly_name: next } : s))
      );
      message.success('Friendly name updated');
    } catch (err: any) {
      message.error(err.message || 'Failed to update friendly name');
    }
  };

  const simColumns = [
    {
      title: 'Identifier',
      dataIndex: 'identifier',
      key: 'identifier',
      render: (id: string, record: SimAssignment) =>
        record.provider === 'vrs' ? (
          <Link to={`/admin/sims/${record.provider}/${id}`}>{id}</Link>
        ) : (
          id
        ),
    },
    {
      title: 'Provider',
      dataIndex: 'provider',
      key: 'provider',
      render: (provider: string) => (
        <Tag color={provider === 'vrs' ? 'blue' : 'green'}>{providerLabel(provider)}</Tag>
      ),
    },
    {
      title: 'MSISDN',
      dataIndex: 'msisdn',
      key: 'msisdn',
      render: (val: string | null) => val || '-',
    },
    {
      title: 'Alias',
      dataIndex: 'alias',
      key: 'alias',
      render: (val: string | null) => val || '-',
    },
    {
      title: 'Friendly Name',
      dataIndex: 'friendly_name',
      key: 'friendly_name',
      render: (val: string | null, record: SimAssignment) => {
        if (!canEditFriendlyName) return val || '-';
        return (
          <Typography.Text
            editable={{
              maxLength: 100,
              tooltip: 'Click to edit',
              onChange: (v: string) => handleFriendlyNameChange(record.id, v),
            }}
          >
            {val || ''}
          </Typography.Text>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (val: string | null) =>
        val ? (
          <Tag color={val === 'active' ? 'green' : val === 'suspended' ? 'orange' : 'default'}>
            {val}
          </Tag>
        ) : (
          '-'
        ),
    },
    {
      title: 'SIM Type',
      dataIndex: 'sim_type',
      key: 'sim_type',
      render: (val: string | null) => (val ? <Tag>{val}</Tag> : '-'),
    },
    {
      title: 'Total Usage',
      key: 'usage',
      render: (_: any, record: SimAssignment) => {
        const s = usageSummaries.find(u => u.iccid === record.identifier);
        return s ? formatBytes(parseFloat(s.total_data_bytes)) : '-';
      },
    },
    {
      title: 'Assigned At',
      dataIndex: 'assigned_at',
      key: 'assigned_at',
      render: (val: string) => (val ? new Date(val).toLocaleString() : '-'),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16 }}>
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/admin/orgs-assets')}
          style={{ paddingLeft: 0 }}
        >
          Back to Org &amp; Assets
        </Button>
      </div>
      <h2 style={{ marginBottom: 16 }}>
        {asset.name} {asset.asset_type && <Tag>{asset.asset_type}</Tag>}{' '}
        <PageHelp
          title="Asset Details Help"
          sections={[
            {
              title: 'Usage Data',
              content:
                'The chart shows data usage for all Data SIMs assigned to this asset over the last 30 days. Each SIM is shown as a stacked bar in a different color.',
            },
            {
              title: 'Crew Management',
              content:
                'Add or remove crew members assigned to this asset. Crew members can be given roles (captain, engineer, etc.) and departments.\n\nAsset managers assigned as crew can see all SIM usage for this asset.',
              tips: [
                'Only users from the same organization can be added as crew',
                'Removing crew does not delete the user \u2014 just removes the asset assignment',
              ],
            },
            {
              title: 'SIM Cards',
              content:
                'Shows all SIM cards (Data and Crew) assigned to this asset. Click an identifier to see the full SIM detail page with usage history.\n\nThe "Total Usage" column shows all-time data consumption for each SIM.',
              tips: [
                'SIMs are assigned to assets via Inventory',
                'Both yacht SIMs and crew SIMs may appear here',
              ],
            },
          ]}
        />
      </h2>

      <Descriptions bordered column={1} style={{ marginBottom: 24 }}>
        <Descriptions.Item label="Name">{asset.name}</Descriptions.Item>
        <Descriptions.Item label="Organization">
          <Space>
            <Link to={`/admin/organizations/${asset.organization_id}`}>
              {asset.org_name || String(asset.organization_id)}
            </Link>
            {canMoveAssetOrg && (
              <Button size="small" onClick={openMoveOrg}>
                Change…
              </Button>
            )}
          </Space>
        </Descriptions.Item>
        <Descriptions.Item label="Type">
          {asset.asset_type ? <Tag>{asset.asset_type}</Tag> : '-'}
        </Descriptions.Item>
        {asset.settings && Object.keys(asset.settings).length > 0 && (
          <Descriptions.Item label="Settings">
            <pre style={{ margin: 0 }}>{JSON.stringify(asset.settings, null, 2)}</pre>
          </Descriptions.Item>
        )}
        <Descriptions.Item label="Status">
          <Tag color={asset.is_active ? 'green' : 'red'}>
            {asset.is_active ? 'Active' : 'Inactive'}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Created">
          {new Date(asset.created_at).toLocaleString()}
        </Descriptions.Item>
        <Descriptions.Item label="Updated">
          {new Date(asset.updated_at).toLocaleString()}
        </Descriptions.Item>
      </Descriptions>

      {/* KAN-19: Vessel logo */}
      <Card title="Vessel logo" style={{ marginBottom: 24 }}>
        <Space size={24} align="start" wrap>
          {asset.logoUrl ? (
            <Image
              src={asset.logoUrl}
              alt={`${asset.name} logo`}
              width={120}
              height={120}
              style={{ objectFit: 'contain', border: '1px solid #f0f0f0', borderRadius: 8 }}
            />
          ) : (
            <Avatar
              shape="square"
              size={120}
              icon={<PictureOutlined />}
              style={{ background: '#fafafa', color: '#bfbfbf' }}
            />
          )}
          <Space direction="vertical" size={8}>
            <Typography.Text type="secondary">
              PNG, JPEG, or WebP · up to 2&nbsp;MB.
              <br />A <strong>square</strong> image works best — recommended at least
              256×256&nbsp;px (e.g. 512×512). Shown at 120×120.
            </Typography.Text>
            {canManageLogo ? (
              <Space>
                <Upload
                  accept="image/png,image/jpeg,image/webp"
                  showUploadList={false}
                  maxCount={1}
                  beforeUpload={validateLogoFile}
                  customRequest={handleLogoUpload}
                  disabled={logoUploading || logoRemoving}
                >
                  <Button icon={<UploadOutlined />} loading={logoUploading}>
                    {asset.logoUrl ? 'Replace logo' : 'Upload logo'}
                  </Button>
                </Upload>
                {asset.logoUrl && (
                  <Popconfirm title="Remove vessel logo?" onConfirm={handleRemoveLogo}>
                    <Button danger loading={logoRemoving} disabled={logoUploading}>
                      Remove
                    </Button>
                  </Popconfirm>
                )}
              </Space>
            ) : (
              <Typography.Text type="secondary">
                You do not have permission to change the logo.
              </Typography.Text>
            )}
          </Space>
        </Space>
      </Card>

      {/* Usage Summary */}
      {simAssignments.some(s => s.provider === 'vrs') && (
        <>
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic
                  title="Total Data (All Time)"
                  value={totalDataBytes > 0 ? formatBytes(totalDataBytes) : '—'}
                  prefix={<CloudDownloadOutlined />}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic
                  title="SIMs with Usage"
                  value={activeSims}
                  suffix={`/ ${simAssignments.filter(s => s.provider === 'vrs').length}`}
                  prefix={<WifiOutlined />}
                />
              </Card>
            </Col>
          </Row>

          {/* Usage Chart */}
          <Card
            title="Data Usage (Last 30 Days)"
            style={{ marginBottom: 24 }}
            loading={usageLoading}
          >
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    label={{ value: 'GB', angle: -90, position: 'insideLeft', fontSize: 12 }}
                  />
                  <Tooltip formatter={(val: number) => [`${val.toFixed(2)} GB`]} />
                  {simLabelArray.length <= 5 && <Legend />}
                  {simLabelArray.map((label, i) => (
                    <Bar
                      key={label}
                      dataKey={label}
                      stackId="usage"
                      fill={SIM_COLORS[i % SIM_COLORS.length]}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                {usageLoading ? 'Loading...' : 'No usage data for the last 30 days'}
              </div>
            )}
          </Card>
        </>
      )}

      {/* Crew Management */}
      <Card
        title={`Crew (${crew.length})`}
        style={{ marginBottom: 24 }}
        extra={
          canManageCrew && (
            <Button type="primary" size="small" icon={<PlusOutlined />} onClick={openAddCrew}>
              Add Crew
            </Button>
          )
        }
        loading={crewLoading}
      >
        {crew.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <YachtUserTree crew={crew} />
          </div>
        )}
        {crew.length > 0 ? (
          <Table
            dataSource={crew}
            rowKey="id"
            size="small"
            pagination={false}
            columns={[
              {
                title: 'Name',
                key: 'name',
                render: (_: any, r: CrewMember) => (
                  <Link to={`/admin/users/${r.user_id}`}>
                    {r.first_name} {r.last_name}
                  </Link>
                ),
              },
              { title: 'Email', dataIndex: 'email', key: 'email' },
              {
                title: (
                  <Space size={4}>
                    Role on Asset
                    <AntTooltip title="Per-yacht job label (captain, engineer, chef, crew, etc.). Informational only — it does NOT grant permissions. A user can have a different role on each yacht.">
                      <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                    </AntTooltip>
                  </Space>
                ),
                dataIndex: 'role_on_asset',
                key: 'role_on_asset',
                render: (val: string) => <Tag color="blue">{val}</Tag>,
              },
              {
                title: 'Department',
                dataIndex: 'department',
                key: 'department',
                render: (val: string | null) => val || '-',
              },
              {
                title: (
                  <Space size={4}>
                    System Role
                    <AntTooltip title="The user's global role (super_admin, asset_manager, crew, etc.). Determines API permissions and data scope across the whole platform.">
                      <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                    </AntTooltip>
                  </Space>
                ),
                dataIndex: 'user_role',
                key: 'user_role',
                render: (val: string) => <Tag>{val}</Tag>,
              },
              ...(canManageCrew
                ? [
                    {
                      title: 'Actions',
                      key: 'actions',
                      render: (_: any, r: CrewMember) => (
                        <Popconfirm
                          title="Remove crew member?"
                          onConfirm={() => handleRemoveCrew(r.user_id)}
                        >
                          <Button type="link" danger size="small">
                            Remove
                          </Button>
                        </Popconfirm>
                      ),
                    },
                  ]
                : []),
            ]}
          />
        ) : (
          <div style={{ color: '#999', textAlign: 'center', padding: 16 }}>
            <UserOutlined style={{ fontSize: 24, marginBottom: 8, display: 'block' }} />
            No crew assigned to this asset
          </div>
        )}
      </Card>

      {/* Add Crew Modal */}
      <Modal
        title="Add Crew Member"
        open={addCrewVisible}
        onCancel={() => setAddCrewVisible(false)}
        footer={null}
      >
        {(() => {
          const eligibleUsers = allUsers.filter(u => !crew.some(c => c.user_id === u.id));
          return (
            <>
              <Segmented
                block
                value={addCrewMode}
                onChange={v => setAddCrewMode(v as 'existing' | 'create')}
                options={[
                  {
                    label: `Add existing${eligibleUsers.length ? ` (${eligibleUsers.length})` : ''}`,
                    value: 'existing',
                    disabled: eligibleUsers.length === 0,
                  },
                  { label: 'Create new', value: 'create' },
                ]}
                style={{ marginBottom: 16 }}
              />
              <Form form={addCrewForm} layout="vertical" onFinish={handleAddCrew}>
                {addCrewMode === 'existing' ? (
                  <Form.Item
                    name="userId"
                    label="User"
                    rules={[{ required: true, message: 'Select a user' }]}
                  >
                    <Select placeholder="Select user" showSearch optionFilterProp="children">
                      {eligibleUsers.map(u => (
                        <Select.Option key={u.id} value={u.id}>
                          {u.first_name} {u.last_name} ({u.email}) - {u.role}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                ) : (
                  <>
                    <Form.Item
                      name="email"
                      label="Email"
                      rules={[
                        { required: true, message: 'Enter an email' },
                        { type: 'email', message: 'Not a valid email' },
                      ]}
                    >
                      <Input placeholder="user@example.com" />
                    </Form.Item>
                    <Form.Item
                      name="firstName"
                      label="First name"
                      rules={[{ required: true, message: 'Enter a first name' }]}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      name="lastName"
                      label="Last name"
                      rules={[{ required: true, message: 'Enter a last name' }]}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      name="userRole"
                      label="Global Role"
                      initialValue="crew"
                      tooltip="System-wide role for the new user. Defaults to 'crew' for a crew member."
                    >
                      <LookupSelect category="role" placeholder="Select global role" />
                    </Form.Item>
                  </>
                )}
                <Form.Item name="roleOnAsset" label="Role on Asset" initialValue="crew">
                  <LookupSelect category="role" placeholder="e.g. captain, crew, engineer" />
                </Form.Item>
                <Form.Item name="department" label="Department">
                  <LookupSelect category="department" placeholder="e.g. deck, engineering" />
                </Form.Item>
                <Form.Item>
                  <Space>
                    <Button type="primary" htmlType="submit" loading={addCrewSubmitting}>
                      {addCrewMode === 'create' ? 'Create + Add' : 'Add'}
                    </Button>
                    <Button onClick={() => setAddCrewVisible(false)} disabled={addCrewSubmitting}>
                      Cancel
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            </>
          );
        })()}
      </Modal>

      <Modal
        title="Move asset to organization"
        open={moveOrgVisible}
        onCancel={() => !moveOrgSubmitting && setMoveOrgVisible(false)}
        onOk={handleMoveOrg}
        okText="Move"
        okButtonProps={{ disabled: !moveOrgTarget, loading: moveOrgSubmitting, danger: true }}
        cancelButtonProps={{ disabled: moveOrgSubmitting }}
        closable={!moveOrgSubmitting}
        maskClosable={!moveOrgSubmitting}
      >
        <p style={{ marginBottom: 12 }}>
          Move <strong>{asset.name}</strong> and everything attached to it ({simAssignments.length}{' '}
          SIM{simAssignments.length === 1 ? '' : 's'}, {crew.length} crew member
          {crew.length === 1 ? '' : 's'}) from{' '}
          <strong>{asset.org_name || `org ${asset.organization_id}`}</strong> to a new organization.
          This re-scopes who can see and manage them.
        </p>
        <Form layout="vertical">
          <Form.Item
            label="Target organization"
            required
            tooltip={
              asset.asset_type === 'yacht'
                ? 'Yachts must belong to a customer organization. Platform and reseller orgs are filtered out.'
                : undefined
            }
          >
            <Select
              showSearch
              placeholder={
                asset.asset_type === 'yacht'
                  ? 'Select customer organization'
                  : 'Select organization'
              }
              value={moveOrgTarget ?? undefined}
              onChange={v => setMoveOrgTarget(v)}
              optionFilterProp="children"
              disabled={moveOrgSubmitting}
            >
              {organizations
                .filter(o => (asset.asset_type === 'yacht' ? o.type === 'customer' : true))
                .map(o => (
                  <Select.Option key={o.id} value={o.id}>
                    {o.name}
                    {asset.asset_type !== 'yacht' ? ` (${o.type})` : ''}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      <h3 style={{ marginBottom: 16 }}>Assigned SIMs ({simAssignments.length})</h3>
      <Table
        columns={simColumns}
        dataSource={simAssignments}
        rowKey="id"
        pagination={{
          defaultPageSize: 20,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (total: number, range: [number, number]) =>
            `${range[0]}-${range[1]} of ${total} items`,
        }}
      />
    </div>
  );
};

export default AssetDetail;
